import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Trash2, X, Heart } from "lucide-react";

const emptyForm = {
  donor_name: "",
  member_id: "",
  amount: "",
  method: "Transfer bancar",
  received_at: new Date().toISOString().slice(0, 10),
  purpose: "",
  notes: "",
};

/**
 * Donations register: track gifts received by the lodge from members or
 * outsiders. Visible alongside dues so the treasurer sees both inflows in
 * one place. Each row carries an optional `member_id` link so a member's
 * profile can later display their giving history.
 */
export default function DonationsPage() {
  const { t, i18n } = useTranslation();
  const en = i18n.language !== "ro";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [lodgeCurrency, setLodgeCurrency] = useState("RON");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/donations");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    api.get("/dues/settings")
      .then(({ data }) => setLodgeCurrency(data?.currency || "RON"))
      .catch(() => {});
    if (isAdmin) {
      api.get("/members").then(({ data }) => setMembers(data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/donations", {
        ...form,
        amount: parseFloat(form.amount),
        currency: lodgeCurrency,
        member_id: form.member_id || null,
      });
      toast.success(t("donations.donation_saved"));
      setShowForm(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(en ? `Delete donation from ${d.donor_name}?` : `Ștergi donația de la ${d.donor_name}?`)) return;
    try {
      await api.delete(`/donations/${d.id}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Everything on this page is displayed in the lodge currency configured
  // on the Dues settings page — one single currency, no per-row exceptions.
  const total = rows.reduce((acc, d) => acc + Number(d.amount || 0), 0);

  return (
    <div data-testid="donations-page">
      <div className="page-header" style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="page-title">{t("donations.donations")}</h1>
          <p className="page-subtitle">
            {t("donations.record_and_manage_charitable_donations")}
          </p>
        </div>
        {isAdmin && (
          <button className="btn-primary-gold flex items-center gap-2" onClick={() => { setForm(emptyForm); setShowForm(true); }} data-testid="add-donation-btn">
            <Plus size={14} /> {t("donations.new_donation")}
          </button>
        )}
      </div>

      <div className="gold-card mb-4 p-4 flex items-center gap-3" data-testid="donations-total">
        <Heart size={22} color="#c9a84c" />
        <div>
          <div className="kv-label" style={{ fontSize: "0.65rem" }}>
            {t("donations.total_donations_registered")}
          </div>
          <div style={{ fontFamily: "Cinzel, serif", fontSize: "1.3rem", color: "#0d1b2a" }}>
            {total.toFixed(2)} <span style={{ fontSize: "0.85rem", color: "#9a7530" }}>{lodgeCurrency}</span>
          </div>
        </div>
      </div>

      <div className="gold-card" style={{ padding: 0, overflow: "auto" }}>
        <table style={{ width: "100%", minWidth: 600, borderCollapse: "collapse", fontFamily: "Crimson Text, serif" }} data-testid="donations-table">
          <thead style={{ background: "#0d1b2a", color: "#e8c97a" }}>
            <tr>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>
                {t("donations.date")}
              </th>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>
                {t("donations.donor")}
              </th>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "right", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>
                {t("donations.amount")}
              </th>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>
                {t("donations.method")}
              </th>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>
                {t("donations.purpose")}
              </th>
              {isAdmin && <th style={{ padding: "0.7rem 0.9rem", textAlign: "right" }} />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 6 : 5} style={{ padding: "2rem", textAlign: "center", color: "#9a7530", fontStyle: "italic" }} data-testid="donations-empty">
                  {t("donations.no_donations_recorded_yet")}
                </td>
              </tr>
            ) : (
              rows.map((d) => (
                <tr key={d.id} style={{ borderBottom: "1px solid #ead8a8" }} data-testid={`donation-row-${d.id}`}>
                  <td style={{ padding: "0.55rem 0.9rem", fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem", color: "#34322d" }}>
                    {d.received_at}
                  </td>
                  <td style={{ padding: "0.55rem 0.9rem", color: "#0d1b2a" }}>
                    {d.donor_name}
                    {d.member_id && (
                      <span className="kv-label" style={{ marginLeft: 6, fontSize: "0.66rem" }}>
                        ({t("donations.member")})
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "0.55rem 0.9rem", textAlign: "right", fontFamily: "Cinzel, serif", color: "#0d1b2a", fontWeight: 600 }}>
                    {Number(d.amount).toFixed(2)} {lodgeCurrency}
                  </td>
                  <td style={{ padding: "0.55rem 0.9rem", color: "#34322d" }}>{d.method || "—"}</td>
                  <td style={{ padding: "0.55rem 0.9rem", color: "#34322d", fontStyle: d.purpose ? "normal" : "italic" }}>
                    {d.purpose || (t("donations.unspecified"))}
                  </td>
                  {isAdmin && (
                    <td style={{ padding: "0.55rem 0.9rem", textAlign: "right" }}>
                      <button className="btn-danger" onClick={() => remove(d)} data-testid={`delete-donation-${d.id}`}>
                        <Trash2 size={11} />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && isAdmin && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content gold-card" style={{ maxWidth: 520, padding: "1.5rem" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
                {t("donations.new_donation")}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500" data-testid="close-donation-form">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="donation-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label-lodge">{t("donations.donor_name")}</label>
                  <input
                    className="input-lodge" required
                    value={form.donor_name}
                    onChange={(e) => setForm({ ...form, donor_name: e.target.value })}
                    data-testid="donation-donor-name"
                  />
                </div>
                <div>
                  <label className="label-lodge">{t("donations.member_optional")}</label>
                  <select
                    className="input-lodge"
                    value={form.member_id}
                    onChange={(e) => {
                      const m = members.find((x) => x.id === e.target.value);
                      setForm({
                        ...form,
                        member_id: e.target.value,
                        donor_name: m ? `${m.first_name} ${m.last_name}` : form.donor_name,
                      });
                    }}
                    data-testid="donation-member-select"
                  >
                    <option value="">{t("donations.external_donor")}</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>{m.last_name} {m.first_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-lodge">{t("donations.amount_2")}</label>
                  <input
                    type="number" step="0.01" min="0.01" className="input-lodge" required
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="donation-amount"
                  />
                </div>
                <div>
                  <label className="label-lodge">{t("donations.currency")}</label>
                  <input
                    className="input-lodge" value={lodgeCurrency} disabled readOnly
                    style={{ opacity: 0.7 }}
                    data-testid="donation-currency"
                  />
                </div>
                <div>
                  <label className="label-lodge">{t("donations.date_received")}</label>
                  <input
                    type="date" className="input-lodge" required
                    value={form.received_at}
                    onChange={(e) => setForm({ ...form, received_at: e.target.value })}
                    data-testid="donation-date"
                  />
                </div>
                <div>
                  <label className="label-lodge">{t("donations.method_2")}</label>
                  <select
                    className="input-lodge"
                    value={form.method}
                    onChange={(e) => setForm({ ...form, method: e.target.value })}
                    data-testid="donation-method"
                  >
                    <option>{t("donations.cash")}</option>
                    <option>{t("donations.bank_transfer")}</option>
                    <option>{t("donations.card")}</option>
                    <option>{t("donations.goods")}</option>
                    <option>{t("donations.other")}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label-lodge">{t("donations.purpose_2")}</label>
                <input
                  type="text" className="input-lodge"
                  placeholder={t("donations.e_g_roof_repair_library")}
                  value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                  data-testid="donation-purpose"
                />
              </div>
              <div>
                <label className="label-lodge">{t("donations.notes")}</label>
                <textarea
                  className="input-lodge" rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="donation-notes"
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>
                  {t("donations.cancel")}
                </button>
                <button type="submit" className="btn-primary-gold" disabled={busy} data-testid="submit-donation">
                  {busy ? "..." : (t("donations.save"))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
